import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import CurrencyInput from '../common/CurrencyInput';
import { formatCurrency } from '../../utils/formatters';
import { calculateMonthlyRepayments } from '../../utils/calculations';
import { BondRepaymentMonthlyResult } from '../../types/calculator.types';

const BondRepaymentMonthly: React.FC = () => {
  const [loanAmount, setLoanAmount] = useState<number>(0);
  const [depositAmount, setDepositAmount] = useState<number>(0);
  const [interestRate, setInterestRate] = useState<number>(10);
  const [results, setResults] = useState<BondRepaymentMonthlyResult[] | null>(null);
  const [totalLoanAmount, setTotalLoanAmount] = useState<number>(0);

  useEffect(() => {
    if (loanAmount > 0 && interestRate > 0 && depositAmount < loanAmount) {
      const calculations = calculateMonthlyRepayments(loanAmount, depositAmount, interestRate);
      setResults(calculations);
      setTotalLoanAmount(loanAmount - depositAmount);
    } else {
      setResults(null);
      setTotalLoanAmount(0);
    }
  }, [loanAmount, depositAmount, interestRate]);

  return (
    <div className="p-2">
      <CurrencyInput
        value={loanAmount}
        onChange={setLoanAmount}
        label="Loan Amount (R)"
        required
      />

      <CurrencyInput
        value={depositAmount}
        onChange={setDepositAmount}
        label="Deposit Amount (R)"
        required
      />

      <Form.Group className="mb-2">
        <div className="form-group has-float-label">
          <Form.Control
            type="number"
            min="0.1"
            max="50"
            step="0.1"
            value={interestRate}
            onChange={(e) => setInterestRate(parseFloat(e.target.value) || 10)}
            required
            placeholder=" "
          />
          <span className="required">Annual Interest Rate (%)</span>
        </div>
      </Form.Group>

      {results && (
        <table className="table table-sm mt-2" style={{ fontSize: '13px' }}>
          <thead>
            <tr>
              <th className="text-secondary border-0 text-end py-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                MONTHLY
              </th>
              <th className="text-secondary border-0 text-end py-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                INTEREST
              </th>
              <th className="text-secondary border-0 text-end py-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                TOTAL
              </th>
            </tr>
          </thead>
          <tbody>
            {results.map((result) => (
              <React.Fragment key={result.term}>
                <tr>
                  <td className="border-0 py-1" colSpan={3}>
                    Over {result.term} years
                  </td>
                </tr>
                <tr>
                  <td className="text-end font-weight-bold border-0 py-1">
                    {formatCurrency(result.monthlyPayment)}
                  </td>
                  <td className="text-end font-weight-bold border-0 py-1">
                    {formatCurrency(result.totalInterest)}
                  </td>
                  <td className="text-end font-weight-bold border-0 py-1">
                    {formatCurrency(result.totalAmount)}
                  </td>
                </tr>
              </React.Fragment>
            ))}
            {depositAmount > 0 && (
              <>
                <tr>
                  <th colSpan={3} className="text-secondary border-0 py-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                    CALCULATED
                  </th>
                </tr>
                <tr>
                  <td className="border-0 py-1">Total Loan Amount</td>
                  <td colSpan={2} className="text-end font-weight-bold border-0 py-1">
                    R {formatCurrency(totalLoanAmount)}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default BondRepaymentMonthly;
